/**
 * FacultyPayoutsPage - Admin management of faculty payouts
 */

import { useState, useEffect, useRef } from 'react';
import { Link } from 'react-router-dom';
import {
  getAdminFacultyPayouts,
  createFacultyPayout,
  processFacultyPayout,
  retryFacultyPayout,
  cancelFacultyPayout,
  formatCurrency,
  searchAdminClasses,
  getPayoutForecast,
  DuplicatePayoutWarning,
  type FacultyPayoutAdmin,
  type AdminClassSearchResult,
  type PayoutForecast,
} from '../../../api/admin';
import SortableHeader from '../../../components/admin/SortableHeader';
import Pagination from '../../../components/admin/Pagination';
import ConfirmModal from '../../../components/ui/ConfirmModal';
import { useDebounce } from '../../../hooks/useDebounce';
import { formatTimeDisplay } from '@sda/shared';

type PayoutStatus = 'all' | 'pending' | 'paid' | 'failed' | 'cancelled';

export default function FacultyPayoutsPage() {
  const [payouts, setPayouts] = useState<FacultyPayoutAdmin[]>([]);
  const [total, setTotal] = useState(0);
  const [summary, setSummary] = useState({
    total_pending_cents: 0,
    total_paid_cents: 0,
    total_failed_count: 0,
    total_failed_cents: 0,
  });
  const [isLoading, setIsLoading] = useState(true);
  const [hasLoaded, setHasLoaded] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [statusFilter, setStatusFilter] = useState<PayoutStatus>('all');
  const [searchQuery, setSearchQuery] = useState('');
  const [page, setPage] = useState(1);
  const [sortBy, setSortBy] = useState('class_date');
  const [sortOrder, setSortOrder] = useState<'asc' | 'desc'>('desc');
  const [processingId, setProcessingId] = useState<string | null>(null);
  const [retryingId, setRetryingId] = useState<string | null>(null);
  const [cancellingId, setCancellingId] = useState<string | null>(null);
  const [confirmCancelId, setConfirmCancelId] = useState<string | null>(null);
  const [showCreateModal, setShowCreateModal] = useState(false);
  const [activeTab, setActiveTab] = useState<'payouts' | 'future'>('payouts');
  const [forecast, setForecast] = useState<PayoutForecast | null>(null);
  const [forecastLoading, setForecastLoading] = useState(false);
  const perPage = 50;

  const debouncedSearch = useDebounce(searchQuery, 300);
  // Latest-wins guard: a slow response for an older query must never
  // overwrite the results of a newer one (same pattern as StudiosListPage).
  const reqIdRef = useRef(0);

  useEffect(() => {
    loadPayouts();
  }, [statusFilter, debouncedSearch, page, sortBy, sortOrder]);

  const loadPayouts = async () => {
    const reqId = ++reqIdRef.current;
    try {
      setIsLoading(true);
      setError(null);
      const result = await getAdminFacultyPayouts({
        page,
        per_page: perPage,
        status: statusFilter !== 'all' ? statusFilter : undefined,
        search: debouncedSearch || undefined,
        sort_by: sortBy,
        sort_order: sortOrder,
      });
      if (reqId !== reqIdRef.current) return;
      setPayouts(result.payouts);
      setTotal(result.total);
      setSummary(result.summary);
    } catch (err) {
      if (reqId !== reqIdRef.current) return;
      setError(err instanceof Error ? err.message : 'Failed to load payouts');
    } finally {
      if (reqId === reqIdRef.current) {
        setIsLoading(false);
        setHasLoaded(true);
      }
    }
  };

  const handleProcess = async (payoutId: string) => {
    try {
      setProcessingId(payoutId);
      await processFacultyPayout(payoutId);
      await loadPayouts();
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Failed to process payout');
    } finally {
      setProcessingId(null);
    }
  };

  // SDA-266: retry a failed payout. A processed:false response is a 200 —
  // the transfer failed again; surface the fresh reason in the page banner.
  const handleRetry = async (payoutId: string) => {
    try {
      setRetryingId(payoutId);
      setError(null);
      const result = await retryFacultyPayout(payoutId);
      // Reload FIRST — loadPayouts resets the error banner, so the fresh
      // failure reason must be set after it settles.
      await loadPayouts();
      if (!result.processed) {
        setError(result.error || 'Retry failed — the payout is marked failed again.');
      }
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Failed to retry payout');
    } finally {
      setRetryingId(null);
    }
  };

  const handleCancel = (payoutId: string) => {
    setConfirmCancelId(payoutId);
  };

  const confirmCancel = async () => {
    const payoutId = confirmCancelId;
    if (!payoutId) return;
    setConfirmCancelId(null);
    try {
      setCancellingId(payoutId);
      await cancelFacultyPayout(payoutId);
      await loadPayouts();
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Failed to cancel payout');
    } finally {
      setCancellingId(null);
    }
  };

  const loadForecast = async () => {
    setForecastLoading(true);
    try {
      const result = await getPayoutForecast();
      setForecast(result);
    } catch { /* silent */ }
    finally { setForecastLoading(false); }
  };

  useEffect(() => {
    if (activeTab === 'future' && !forecast) loadForecast();
  }, [activeTab]); // eslint-disable-line react-hooks/exhaustive-deps

  const handleSort = (field: string) => {
    if (sortBy === field) {
      setSortOrder((prev) => (prev === 'asc' ? 'desc' : 'asc'));
    } else {
      setSortBy(field);
      setSortOrder('asc');
    }
    setPage(1);
  };

  const getStatusBadge = (status: string) => {
    const styles: Record<string, string> = {
      pending: 'bg-warning-100 text-warning-700',
      paid: 'bg-success-100 text-success-700',
      failed: 'bg-danger-100 text-danger-700',
      cancelled: 'bg-warm-200 text-warm-600',
    };
    return (
      <span className={`px-2 py-0.5 rounded-full text-xs font-medium ${styles[status] || 'bg-warm-100 text-warm-600'}`}>
        {status.charAt(0).toUpperCase() + status.slice(1)}
      </span>
    );
  };

  const formatDate = (date: string | null) => {
    if (!date) return '--';
    const dateStr = date.includes('T') ? date : date + 'T12:00:00';
    return new Date(dateStr).toLocaleDateString('en-US', {
      month: 'short',
      day: 'numeric',
      year: 'numeric',
    });
  };

  // One-off payment type chip (SDA-221) — styled like the "Auto" badge
  const typeChip = (paymentType: string, note: string | null) => (
    <span
      className="px-1.5 py-0.5 bg-purple-50 text-purple-700 text-xs font-medium rounded"
      title={note || undefined}
    >
      {paymentType === 'reimbursement' ? 'Reimbursement' : paymentType === 'bonus' ? 'Bonus' : 'One-off'}
    </span>
  );

  if (isLoading && !hasLoaded) {
    return (
      <div>
        <div className="flex items-center justify-between mb-8">
          <div className="h-10 bg-warm-200 rounded w-1/4 animate-pulse" />
          <div className="h-10 bg-warm-200 rounded w-32 animate-pulse" />
        </div>
        <div className="grid grid-cols-1 sm:grid-cols-3 gap-4 mb-6">
          {[1, 2, 3].map((i) => (
            <div key={i} className="bg-white rounded-xl shadow-sm p-6 animate-pulse">
              <div className="h-4 bg-warm-100 rounded w-1/2 mb-3" />
              <div className="h-8 bg-warm-100 rounded w-2/3" />
            </div>
          ))}
        </div>
        <div className="bg-white rounded-xl shadow-sm">
          {[1, 2, 3, 4, 5].map((i) => (
            <div key={i} className="p-6 border-b border-warm-100 animate-pulse">
              <div className="h-6 bg-warm-100 rounded w-1/3 mb-2" />
              <div className="h-4 bg-warm-100 rounded w-1/2" />
            </div>
          ))}
        </div>
      </div>
    );
  }

  return (
    <div>
      {/* Header */}
      <div className="flex flex-col sm:flex-row sm:items-center justify-between gap-4 mb-8">
        <div>
          <h1 className="text-2xl sm:text-3xl font-display font-bold text-warm-900">Faculty Payouts</h1>
          <p className="text-warm-600 mt-1 hidden sm:block">
            Manage faculty payout scheduling and processing.
          </p>
        </div>
        <button
          onClick={() => setShowCreateModal(true)}
          className="btn-primary min-h-[44px] flex items-center"
        >
          <svg className="w-5 h-5 mr-2" fill="none" viewBox="0 0 24 24" stroke="currentColor">
            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 4v16m8-8H4" />
          </svg>
          Create Payout
        </button>
      </div>

      {/* Summary Cards */}
      <div className="grid grid-cols-1 sm:grid-cols-3 gap-4 mb-6">
        <div className="bg-white rounded-xl shadow-sm p-6 border-l-4 border-warning-400">
          <p className="text-sm font-medium text-warm-600">Total Pending</p>
          <p className="text-2xl font-bold text-warning-700 mt-1">
            {formatCurrency(summary.total_pending_cents)}
          </p>
        </div>
        <div className="bg-white rounded-xl shadow-sm p-6 border-l-4 border-success-400">
          <p className="text-sm font-medium text-warm-600">Total Paid</p>
          <p className="text-2xl font-bold text-success-700 mt-1">
            {formatCurrency(summary.total_paid_cents)}
          </p>
        </div>
        <div className="bg-white rounded-xl shadow-sm p-6 border-l-4 border-danger-400">
          <p className="text-sm font-medium text-warm-600">Total Failed</p>
          <p className="text-2xl font-bold text-danger-700 mt-1">
            {summary.total_failed_count}
          </p>
        </div>
      </div>

      {/* Tabs */}
      <div className="flex gap-1 mb-6 bg-warm-100 p-1 rounded-lg w-fit">
        <button
          onClick={() => setActiveTab('payouts')}
          className={`px-4 py-2 text-sm font-medium rounded-md transition-colors ${activeTab === 'payouts' ? 'bg-white text-warm-900 shadow-sm' : 'text-warm-500 hover:text-warm-700'}`}
        >
          All Payouts
        </button>
        <button
          onClick={() => setActiveTab('future')}
          className={`px-4 py-2 text-sm font-medium rounded-md transition-colors ${activeTab === 'future' ? 'bg-white text-warm-900 shadow-sm' : 'text-warm-500 hover:text-warm-700'}`}
        >
          Future Payments
        </button>
      </div>

      {activeTab === 'payouts' && (
      <>
      {/* Filter Bar */}
      <div className="bg-white rounded-xl shadow-sm p-4 mb-6">
        <div className="flex flex-col sm:flex-row gap-3">
          <div className="w-full sm:flex-1">
            <label className="block text-sm font-medium text-warm-700 mb-1">Search</label>
            <div className="relative">
              <input
                type="text"
                value={searchQuery}
                onChange={(e) => {
                  setSearchQuery(e.target.value);
                  setPage(1);
                }}
                placeholder="Search faculty or booking #..."
                className="w-full px-3 py-2.5 pr-9 border border-warm-200 rounded-lg focus:ring-2 focus:ring-primary-500 focus:border-transparent"
              />
              {isLoading && hasLoaded && (
                <svg className="absolute right-3 top-1/2 -translate-y-1/2 w-4 h-4 text-primary-500 animate-spin" viewBox="0 0 24 24" fill="none" aria-label="Searching">
                  <circle className="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="4" />
                  <path className="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8v4a4 4 0 00-4 4H4z" />
                </svg>
              )}
            </div>
          </div>
          <div className="w-full sm:min-w-[180px] sm:w-auto">
            <label className="block text-sm font-medium text-warm-700 mb-1">Status</label>
            <select
              value={statusFilter}
              onChange={(e) => {
                setStatusFilter(e.target.value as PayoutStatus);
                setPage(1);
              }}
              className="w-full px-3 py-2.5 border border-warm-200 rounded-lg focus:ring-2 focus:ring-primary-500 focus:border-transparent"
            >
              <option value="all">All Status</option>
              <option value="pending">Pending</option>
              <option value="paid">Paid</option>
              <option value="failed">Failed</option>
              <option value="cancelled">Cancelled</option>
            </select>
          </div>
          {(statusFilter !== 'all' || searchQuery) && (
            <div className="flex items-end">
              <button
                onClick={() => {
                  setStatusFilter('all');
                  setSearchQuery('');
                  setPage(1);
                }}
                className="px-4 py-2.5 min-h-[44px] text-sm text-warm-600 hover:text-warm-900 hover:bg-warm-100 rounded-lg transition-colors"
              >
                Clear Filters
              </button>
            </div>
          )}
        </div>
      </div>

      {error && (
        <div className="mb-6 p-4 bg-danger-50 border border-danger-200 rounded-lg text-danger-700">
          {error}
        </div>
      )}

      {/* During refetch the previous rows stay mounted, dimmed under a spinner
          overlay, so a search or filter change never blanks the list and
          repaints (same pattern as BookingsListPage). */}
      <div className="relative">
        {isLoading && hasLoaded && (
          <div className="absolute inset-0 z-10 flex items-center justify-center bg-white/50 rounded-xl">
            <svg className="w-8 h-8 text-primary-500 animate-spin" viewBox="0 0 24 24" fill="none" aria-label="Loading payouts">
              <circle className="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="4" />
              <path className="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8v4a4 4 0 00-4 4H4z" />
            </svg>
          </div>
        )}

      {/* Payouts Table */}
      {payouts.length === 0 ? (
        <div className="bg-white rounded-xl shadow-sm p-12 text-center">
          <div className="w-16 h-16 mx-auto mb-4 rounded-full bg-warm-100 flex items-center justify-center">
            <svg className="w-8 h-8 text-warm-400" fill="none" viewBox="0 0 24 24" stroke="currentColor">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 8c-1.657 0-3 .895-3 2s1.343 2 3 2 3 .895 3 2-1.343 2-3 2m0-8c1.11 0 2.08.402 2.599 1M12 8V7m0 1v8m0 0v1m0-1c-1.11 0-2.08-.402-2.599-1M21 12a9 9 0 11-18 0 9 9 0 0118 0z" />
            </svg>
          </div>
          <h3 className="text-lg font-semibold text-warm-900 mb-2">No payouts found</h3>
          <p className="text-warm-600 mb-6">
            {statusFilter !== 'all' || searchQuery
              ? 'Try adjusting your filters to see more results.'
              : 'Faculty payouts will appear here once created.'}
          </p>
        </div>
      ) : (
        <>
          {/* Desktop Table */}
          <div className="hidden md:block bg-white rounded-xl shadow-sm overflow-hidden">
            <table className="w-full">
              <thead className="bg-warm-50 border-b border-warm-100">
                <tr>
                  <SortableHeader label="Faculty" field="faculty_name" currentSort={sortBy} currentOrder={sortOrder} onSort={handleSort} />
                  <th className="text-left px-6 py-4 text-sm font-semibold text-warm-700">Show</th>
                  <SortableHeader label="Class Date" field="class_date" currentSort={sortBy} currentOrder={sortOrder} onSort={handleSort} />
                  <th className="text-left px-6 py-4 text-sm font-semibold text-warm-700">Booking</th>
                  <SortableHeader label="Amount" field="amount_cents" currentSort={sortBy} currentOrder={sortOrder} onSort={handleSort} />
                  <SortableHeader label="Status" field="status" currentSort={sortBy} currentOrder={sortOrder} onSort={handleSort} />
                  <SortableHeader label="Scheduled For" field="scheduled_for" currentSort={sortBy} currentOrder={sortOrder} onSort={handleSort} />
                  <th className="text-right px-6 py-4 text-sm font-semibold text-warm-700">Actions</th>
                </tr>
              </thead>
              <tbody className="divide-y divide-warm-100">
                {payouts.map((payout) => (
                  <tr key={payout.id} className="hover:bg-warm-50 transition-colors">
                    <td className="px-6 py-4">
                      <p className="font-medium text-warm-900">
                        {payout.faculty_name}
                        {payout.source === 'auto' && (
                          <span className="ml-2 px-1.5 py-0.5 bg-primary-50 text-primary-600 text-xs font-medium rounded">Auto</span>
                        )}
                      </p>
                    </td>
                    <td className="px-6 py-4">
                      {payout.class_id === null
                        ? typeChip(payout.payment_type, payout.note)
                        : <span className="text-warm-700">{payout.show_name || '--'}</span>}
                    </td>
                    <td className="px-6 py-4">
                      <span className="text-warm-600 text-sm">{formatDate(payout.class_date)}</span>
                    </td>
                    <td className="px-6 py-4">
                      {payout.booking_id ? (
                        <Link to={`/admin/bookings/${payout.booking_id}`} className="text-primary-600 hover:text-primary-700 text-sm font-medium hover:underline">
                          {payout.booking_number || 'View'}
                        </Link>
                      ) : (
                        <span className="text-warm-400 text-sm">--</span>
                      )}
                    </td>
                    <td className="px-6 py-4">
                      <span className="font-semibold text-warm-900">{formatCurrency(payout.amount_cents)}</span>
                    </td>
                    <td className="px-6 py-4">{getStatusBadge(payout.status)}</td>
                    <td className="px-6 py-4">
                      <span className="text-warm-600 text-sm">{formatDate(payout.scheduled_for)}</span>
                    </td>
                    <td className="px-6 py-4 text-right">
                      {payout.status === 'pending' && (
                        <div className="flex items-center justify-end gap-2">
                          <button
                            onClick={() => handleCancel(payout.id)}
                            disabled={isLoading || cancellingId === payout.id}
                            className="px-3 py-1.5 text-sm font-medium border border-danger-200 text-danger-600 hover:bg-danger-50 rounded-lg transition-colors disabled:opacity-50 disabled:cursor-not-allowed"
                          >
                            {cancellingId === payout.id ? 'Cancelling...' : 'Cancel'}
                          </button>
                          <button
                            onClick={() => handleProcess(payout.id)}
                            disabled={isLoading || processingId === payout.id}
                            className="px-3 py-1.5 text-sm font-medium text-white bg-success-600 hover:bg-success-700 rounded-lg transition-colors disabled:opacity-50 disabled:cursor-not-allowed"
                          >
                            {processingId === payout.id ? (
                              <span className="flex items-center gap-1.5">
                                <svg className="w-4 h-4 animate-spin" fill="none" viewBox="0 0 24 24">
                                  <circle className="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="4" />
                                  <path className="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z" />
                                </svg>
                                Processing
                              </span>
                            ) : (
                              'Process'
                            )}
                          </button>
                        </div>
                      )}
                      {payout.status === 'failed' && (
                        <div className="flex flex-col items-end gap-1.5">
                          {payout.failure_reason && (
                            <span
                              className="block max-w-[240px] truncate text-xs text-danger-600"
                              title={payout.failure_reason}
                            >
                              {payout.failure_reason}
                            </span>
                          )}
                          <button
                            onClick={() => handleRetry(payout.id)}
                            disabled={isLoading || retryingId === payout.id}
                            className="px-3 py-1.5 text-sm font-medium text-white bg-primary-600 hover:bg-primary-700 rounded-lg transition-colors disabled:opacity-50 disabled:cursor-not-allowed"
                          >
                            {retryingId === payout.id ? (
                              <span className="flex items-center gap-1.5">
                                <svg className="w-4 h-4 animate-spin" fill="none" viewBox="0 0 24 24">
                                  <circle className="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="4" />
                                  <path className="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z" />
                                </svg>
                                Retrying
                              </span>
                            ) : (
                              'Retry'
                            )}
                          </button>
                        </div>
                      )}
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>

          {/* Mobile Cards */}
          <div className="md:hidden space-y-3">
            {payouts.map((payout) => (
              <div
                key={payout.id}
                className="bg-white rounded-xl border border-warm-200 p-4"
              >
                <div className="flex items-center justify-between mb-2">
                  {getStatusBadge(payout.status)}
                  <span className="font-semibold text-warm-900">
                    {formatCurrency(payout.amount_cents)}
                  </span>
                </div>
                <p className="font-medium text-warm-900">
                  {payout.faculty_name}
                  {payout.source === 'auto' && (
                    <span className="ml-2 px-1.5 py-0.5 bg-primary-50 text-primary-600 text-xs font-medium rounded">Auto</span>
                  )}
                </p>
                {payout.class_id === null ? (
                  <p className="text-sm">{typeChip(payout.payment_type, payout.note)}</p>
                ) : (
                  <p className="text-sm text-warm-600">{payout.show_name || '--'}</p>
                )}
                <div className="flex items-center justify-between mt-2 text-xs text-warm-500">
                  <span>Class: {formatDate(payout.class_date)}</span>
                  <span>Scheduled: {formatDate(payout.scheduled_for)}</span>
                </div>
                {payout.status === 'pending' && (
                  <div className="mt-3 flex gap-2">
                    <button
                      onClick={() => handleCancel(payout.id)}
                      disabled={isLoading || cancellingId === payout.id}
                      className="flex-1 px-3 py-2.5 min-h-[44px] text-sm font-medium border border-danger-200 text-danger-600 hover:bg-danger-50 rounded-lg transition-colors disabled:opacity-50 disabled:cursor-not-allowed"
                    >
                      {cancellingId === payout.id ? 'Cancelling...' : 'Cancel'}
                    </button>
                    <button
                      onClick={() => handleProcess(payout.id)}
                      disabled={isLoading || processingId === payout.id}
                      className="flex-1 px-3 py-2.5 min-h-[44px] text-sm font-medium text-white bg-success-600 hover:bg-success-700 rounded-lg transition-colors disabled:opacity-50 disabled:cursor-not-allowed"
                    >
                      {processingId === payout.id ? 'Processing...' : 'Process Payout'}
                    </button>
                  </div>
                )}
                {payout.status === 'failed' && (
                  <>
                    {payout.failure_reason && (
                      <p className="mt-2 text-xs text-danger-600">{payout.failure_reason}</p>
                    )}
                    <div className="mt-3">
                      <button
                        onClick={() => handleRetry(payout.id)}
                        disabled={isLoading || retryingId === payout.id}
                        className="w-full px-3 py-2.5 min-h-[44px] text-sm font-medium text-white bg-primary-600 hover:bg-primary-700 rounded-lg transition-colors disabled:opacity-50 disabled:cursor-not-allowed"
                      >
                        {retryingId === payout.id ? 'Retrying...' : 'Retry Payout'}
                      </button>
                    </div>
                  </>
                )}
              </div>
            ))}
          </div>

          {/* Pagination */}
          <Pagination
            page={page}
            total={total}
            limit={perPage}
            itemLabel="payouts"
            onPageChange={setPage}
          />
        </>
      )}
      </div>

      </>
      )}

      {/* Future Payments Tab */}
      {activeTab === 'future' && (
        <div className="space-y-6">
          {forecastLoading ? (
            <div className="bg-white rounded-xl shadow-sm p-8">
              <div className="space-y-3">
                {[1,2,3,4].map(i => <div key={i} className="h-12 bg-warm-100 rounded-lg animate-pulse" />)}
              </div>
            </div>
          ) : forecast ? (
            <>
              {/* Forecast Summary — distinct style from top cards */}
              <div className="bg-warm-800 rounded-xl p-5 text-white">
                <div className="grid grid-cols-2 gap-6">
                  <div>
                    <p className="text-xs text-warm-400 uppercase tracking-wide font-semibold">Scheduled Pending</p>
                    <p className="text-2xl font-bold mt-1">{formatCurrency(forecast.total_pending_cents)}</p>
                    <p className="text-xs text-warm-400 mt-1">{forecast.pending_payouts.length} payouts waiting to process</p>
                  </div>
                  <div>
                    <p className="text-xs text-warm-400 uppercase tracking-wide font-semibold">Upcoming Forecast</p>
                    <p className="text-2xl font-bold mt-1">{formatCurrency(forecast.total_forecast_cents)}</p>
                    <p className="text-xs text-warm-400 mt-1">{forecast.upcoming_classes.length} classes will generate payouts</p>
                  </div>
                </div>
              </div>

              {/* Pending Payouts Section */}
              {forecast.pending_payouts.length > 0 && (
                <div className="bg-white rounded-xl shadow-sm">
                  <div className="p-4 border-b border-warm-100">
                    <h3 className="text-sm font-semibold text-warm-900 uppercase tracking-wide">Scheduled Pending Payouts</h3>
                    <p className="text-xs text-warm-400 mt-0.5">These will process automatically on their scheduled date</p>
                  </div>
                  <div className="divide-y divide-warm-50">
                    {forecast.pending_payouts.map(p => (
                      <div key={p.id} className="px-4 py-3 flex items-center justify-between">
                        <div>
                          <p className="text-sm font-medium text-warm-900">{p.faculty_name || 'Unknown'}</p>
                          <p className="text-xs text-warm-500">
                            {p.payment_type && p.payment_type !== 'class' ? (
                              <>
                                {p.payment_type === 'reimbursement' ? 'Reimbursement' : p.payment_type === 'bonus' ? 'Bonus' : 'One-off payment'}
                                {p.note ? ` — ${p.note}` : ''}
                              </>
                            ) : (
                              <>{p.show_name} · {formatDate(p.class_date)} · {p.studio_name}</>
                            )}
                            {p.booking_id && (
                              <Link to={`/admin/bookings/${p.booking_id}`} className="ml-2 text-primary-600 hover:underline">
                                {p.booking_number ? `#${p.booking_number}` : 'View booking'}
                              </Link>
                            )}
                          </p>
                        </div>
                        <div className="text-right">
                          <p className="text-sm font-bold text-warm-900">{formatCurrency(p.amount_cents)}</p>
                          <p className="text-xs text-warm-400">Scheduled: {formatDate(p.scheduled_for)}</p>
                        </div>
                      </div>
                    ))}
                  </div>
                </div>
              )}

              {/* Upcoming Classes (Forecast) */}
              {forecast.upcoming_classes.length > 0 && (
                <div className="bg-white rounded-xl shadow-sm">
                  <div className="p-4 border-b border-warm-100">
                    <h3 className="text-sm font-semibold text-warm-900 uppercase tracking-wide">Upcoming Classes (Payout Forecast)</h3>
                    <p className="text-xs text-warm-400 mt-0.5">These classes will generate payouts when marked as completed</p>
                  </div>
                  <div className="divide-y divide-warm-50">
                    {forecast.upcoming_classes.map(c => (
                      <div key={c.class_id} className="px-4 py-3 flex items-center justify-between">
                        <div>
                          <p className="text-sm font-medium text-warm-900">{c.faculty_name || 'Unassigned'}</p>
                          <p className="text-xs text-warm-500">
                            {c.show_name} · {formatDate(c.class_date)} · {c.studio_name}
                            {c.booking_id && (
                              <Link to={`/admin/bookings/${c.booking_id}`} className="ml-2 text-primary-600 hover:underline">
                                {c.booking_number ? `#${c.booking_number}` : 'View booking'}
                              </Link>
                            )}
                          </p>
                        </div>
                        <div className="text-right">
                          <p className="text-sm font-semibold text-blue-700">{formatCurrency(c.expected_amount_cents)}</p>
                          <p className="text-xs text-warm-400">{c.status}</p>
                        </div>
                      </div>
                    ))}
                  </div>
                </div>
              )}

              {forecast.pending_payouts.length === 0 && forecast.upcoming_classes.length === 0 && (
                <div className="bg-white rounded-xl shadow-sm p-12 text-center">
                  <p className="text-warm-500">No upcoming payouts or classes with faculty assigned</p>
                </div>
              )}

              <button
                onClick={loadForecast}
                disabled={forecastLoading}
                className="text-sm text-primary-600 hover:underline disabled:opacity-50"
              >
                Refresh forecast
              </button>
            </>
          ) : (
            <div className="bg-white rounded-xl shadow-sm p-12 text-center">
              <p className="text-warm-500">Failed to load forecast</p>
              <button onClick={loadForecast} className="mt-2 text-sm text-primary-600 hover:underline">Try again</button>
            </div>
          )}
        </div>
      )}

      {/* Create Payout Modal */}
      {showCreateModal && (
        <CreatePayoutModal
          onClose={() => setShowCreateModal(false)}
          onCreated={() => {
            setShowCreateModal(false);
            loadPayouts();
          }}
        />
      )}

      {/* Cancel Payout Confirm */}
      {confirmCancelId && (
        <ConfirmModal
          title="Cancel this payout?"
          message="The payout will be marked as cancelled and will not be processed."
          confirmLabel="Cancel Payout"
          confirmVariant="danger"
          onConfirm={confirmCancel}
          onCancel={() => setConfirmCancelId(null)}
        />
      )}
    </div>
  );
}

function CreatePayoutModal({
  onClose,
  onCreated,
}: {
  onClose: () => void;
  onCreated: () => void;
}) {
  const [selectedClass, setSelectedClass] = useState<AdminClassSearchResult | null>(null);
  const [classSearchQuery, setClassSearchQuery] = useState('');
  const [classSearchResults, setClassSearchResults] = useState<AdminClassSearchResult[]>([]);
  const [isSearchingClasses, setIsSearchingClasses] = useState(false);
  const [amountCents, setAmountCents] = useState('');
  const [scheduledFor, setScheduledFor] = useState('');
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [duplicateWarning, setDuplicateWarning] = useState<string | null>(null);
  const [duplicateStatus, setDuplicateStatus] = useState<string | null>(null);

  // SDA-266: "Create Anyway" only makes sense when the existing payout is
  // dead (failed/cancelled) — the DB unique index blocks a second active row
  // for pending/processing/paid, so force-create would just 409 again.
  const canForceCreate =
    duplicateStatus === 'failed' || duplicateStatus === 'cancelled';

  useEffect(() => {
    if (!classSearchQuery.trim() || classSearchQuery.length < 2) {
      setClassSearchResults([]);
      return;
    }
    const timer = setTimeout(async () => {
      setIsSearchingClasses(true);
      try {
        const result = await searchAdminClasses({
          search: classSearchQuery,
          has_faculty: true,
          faculty_paid: false,
        });
        setClassSearchResults(result.classes);
      } catch {
        setClassSearchResults([]);
      } finally {
        setIsSearchingClasses(false);
      }
    }, 300);
    return () => clearTimeout(timer);
  }, [classSearchQuery]);

  const handleSelectClass = (cls: AdminClassSearchResult) => {
    setSelectedClass(cls);
    setAmountCents(String((cls.faculty_rate_cents || 25000) / 100));
    setClassSearchQuery('');
    setClassSearchResults([]);
  };

  const handleClearClass = () => {
    setSelectedClass(null);
    setAmountCents('');
  };

  const handleClose = () => {
    setSelectedClass(null);
    setClassSearchQuery('');
    setClassSearchResults([]);
    onClose();
  };

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    if (!selectedClass) {
      setError('Please select a class');
      return;
    }

    try {
      setIsSubmitting(true);
      setError(null);
      const forcing = !!duplicateWarning && canForceCreate;
      await createFacultyPayout({
        class_id: selectedClass.id,
        amount_cents: amountCents ? Math.round(parseFloat(amountCents) * 100) : undefined,
        scheduled_for: scheduledFor || undefined,
        force_create: forcing,
      });
      setDuplicateWarning(null);
      setDuplicateStatus(null);
      onCreated();
    } catch (err) {
      if (err instanceof DuplicatePayoutWarning) {
        if (duplicateWarning) {
          // force_create still hit the DB unique index (a pending/paid row
          // exists) — surface the server message verbatim as a hard error.
          setDuplicateWarning(null);
          setDuplicateStatus(null);
          setError(err.message);
        } else {
          setDuplicateWarning(err.message);
          setDuplicateStatus(err.existingStatus);
          setError(null);
        }
      } else {
        setError(err instanceof Error ? err.message : 'Failed to create payout');
      }
    } finally {
      setIsSubmitting(false);
    }
  };

  const formatClassDate = (date: string) => {
    const dateStr = date.includes('T') ? date : date + 'T12:00:00';
    return new Date(dateStr).toLocaleDateString('en-US', {
      month: 'short',
      day: 'numeric',
      year: 'numeric',
    });
  };

  return (
    <div className="fixed inset-0 bg-black/50 z-50 flex items-center justify-center p-4">
      <div className="bg-white rounded-xl shadow-xl max-w-md w-full p-6">
        <div className="flex items-center justify-between mb-6">
          <h2 className="text-lg font-display font-bold text-warm-900">Create Payout</h2>
          <button
            onClick={handleClose}
            className="text-warm-400 hover:text-warm-600 transition-colors"
          >
            <svg className="w-6 h-6" fill="none" viewBox="0 0 24 24" stroke="currentColor">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" />
            </svg>
          </button>
        </div>

        {error && (
          <div className="mb-4 p-3 bg-danger-50 border border-danger-200 rounded-lg text-danger-700 text-sm">
            {error}
          </div>
        )}

        <form onSubmit={handleSubmit} className="space-y-4">
          <div>
            <label className="block text-sm font-medium text-warm-700 mb-1">
              Class <span className="text-danger-500">*</span>
            </label>
            {selectedClass ? (
              <div className="border border-warm-200 rounded-lg bg-warm-50 p-3">
                <div className="flex items-start justify-between">
                  <div className="min-w-0">
                    <p className="font-semibold text-warm-900 truncate">
                      {selectedClass.show_name || 'Unknown Show'}
                    </p>
                    <p className="text-sm text-warm-600">
                      {formatClassDate(selectedClass.class_date)} &middot; {formatTimeDisplay(selectedClass.start_time)}
                    </p>
                    <p className="text-sm text-warm-600">
                      {selectedClass.faculty_name || 'No faculty'}
                    </p>
                    {selectedClass.studio_name && (
                      <p className="text-xs text-warm-500 mt-0.5">
                        {selectedClass.studio_name}
                        {selectedClass.booking_number && ` (#${selectedClass.booking_number})`}
                      </p>
                    )}
                  </div>
                  <button
                    type="button"
                    onClick={handleClearClass}
                    className="ml-2 p-1 text-warm-400 hover:text-warm-600 transition-colors flex-shrink-0"
                  >
                    <svg className="w-4 h-4" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" />
                    </svg>
                  </button>
                </div>
              </div>
            ) : (
              <div className="relative">
                <input
                  type="text"
                  value={classSearchQuery}
                  onChange={(e) => setClassSearchQuery(e.target.value)}
                  placeholder="Search by show, studio, or booking #..."
                  className="w-full px-3 py-2.5 border border-warm-200 rounded-lg focus:ring-2 focus:ring-primary-500 focus:border-transparent"
                />
                {isSearchingClasses && (
                  <div className="absolute right-3 top-1/2 -translate-y-1/2">
                    <svg className="w-4 h-4 animate-spin text-warm-400" fill="none" viewBox="0 0 24 24">
                      <circle className="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="4" />
                      <path className="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z" />
                    </svg>
                  </div>
                )}
                {classSearchQuery.length >= 2 && !isSearchingClasses && classSearchResults.length === 0 && (
                  <div className="absolute z-10 w-full mt-1 border border-warm-200 rounded-lg bg-white shadow-lg p-3 text-sm text-warm-500">
                    No unpaid classes found
                  </div>
                )}
                {classSearchResults.length > 0 && (
                  <div className="absolute z-10 w-full mt-1 border border-warm-200 rounded-lg bg-white shadow-lg max-h-48 overflow-y-auto">
                    {classSearchResults.map((cls) => (
                      <button
                        key={cls.id}
                        type="button"
                        onClick={() => handleSelectClass(cls)}
                        className="w-full text-left px-3 py-2.5 hover:bg-warm-100 border-b border-warm-100 last:border-b-0 transition-colors"
                      >
                        <p className="font-semibold text-warm-900 text-sm">
                          {cls.show_name || 'Unknown Show'}
                          <span className="font-normal text-warm-500 ml-2">
                            {formatClassDate(cls.class_date)} {formatTimeDisplay(cls.start_time)}
                          </span>
                        </p>
                        <p className="text-xs text-warm-600">
                          {cls.studio_name || 'No studio'}
                          {cls.booking_number && ` (#${cls.booking_number})`}
                          {' \u00b7 '}
                          {cls.faculty_name || 'No faculty'}
                          {cls.faculty_rate_cents != null && (
                            <span className="ml-1 text-warm-500">
                              ${(cls.faculty_rate_cents / 100).toFixed(2)}
                            </span>
                          )}
                        </p>
                      </button>
                    ))}
                  </div>
                )}
              </div>
            )}
          </div>

          <div>
            <label className="block text-sm font-medium text-warm-700 mb-1">
              Amount Override ($)
            </label>
            <input
              type="number"
              value={amountCents}
              onChange={(e) => setAmountCents(e.target.value)}
              placeholder="Leave blank for default"
              step="0.01"
              min="0"
              className="w-full px-3 py-2.5 border border-warm-200 rounded-lg focus:ring-2 focus:ring-primary-500 focus:border-transparent"
            />
            <p className="text-xs text-warm-500 mt-1">Optional. Leave blank to use the default payout amount.</p>
          </div>

          <div>
            <label className="block text-sm font-medium text-warm-700 mb-1">
              Scheduled For
            </label>
            <input
              type="date"
              value={scheduledFor}
              onChange={(e) => setScheduledFor(e.target.value)}
              className="w-full px-3 py-2.5 border border-warm-200 rounded-lg focus:ring-2 focus:ring-primary-500 focus:border-transparent"
            />
            <p className="text-xs text-warm-500 mt-1">Optional. When the payout should be processed.</p>
          </div>

          <div className="flex flex-wrap gap-3 pt-2">
            {duplicateWarning && (
              <div className="w-full p-3 bg-amber-50 border border-amber-200 rounded-lg text-xs text-amber-800 mb-2">
                <p className="font-semibold mb-1">A payout already exists for this class</p>
                <p>{duplicateWarning}</p>
                {canForceCreate ? (
                  <p className="mt-1 text-amber-600">Click "Create Anyway" to proceed.</p>
                ) : duplicateStatus === 'paid' ? (
                  <p className="mt-1 text-amber-600">
                    This class has already been paid out — a new payout can't be created.
                  </p>
                ) : (
                  <p className="mt-1 text-amber-600">
                    Cancel the existing payout on the Payouts page first if you need to recreate it.
                  </p>
                )}
              </div>
            )}
            <button
              type="button"
              onClick={handleClose}
              className="flex-1 px-4 py-2.5 min-h-[44px] border border-warm-300 text-warm-700 rounded-lg hover:bg-warm-50 transition-colors"
            >
              Cancel
            </button>
            {(!duplicateWarning || canForceCreate) && (
              <button
                type="submit"
                disabled={isSubmitting || !selectedClass}
                className="flex-1 btn-primary min-h-[44px] disabled:opacity-50 disabled:cursor-not-allowed"
              >
                {isSubmitting ? 'Creating...' : duplicateWarning ? 'Create Anyway' : 'Create Payout'}
              </button>
            )}
          </div>
        </form>
      </div>
    </div>
  );
}
